import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import { Eye, EyeOff, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { extractAxiosError } from '../components/SafeError';
import AuthHeader from '../components/AuthHeader';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const token = searchParams.get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Invalid reset link');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Password validation - must match registration requirements
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/[a-zA-Z]/.test(password)) {
      setError('Password must contain at least one letter');
      return;
    }
    if (!/\d/.test(password)) {
      setError('Password must contain at least one number');
      return;
    }

    setLoading(true);

    try {
      await authApi.resetPassword({ token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(extractAxiosError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen auth-bg flex items-center justify-center p-4" style={{ backgroundImage: 'url(/cockpit/hero.jpg)' }}>
        <div className="absolute inset-0 bg-black/80" />
        
        <div className="relative w-full max-w-md" data-testid="reset-invalid">
          <div className="card-panel p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-status-error/20 rounded-full mb-4">
              <XCircle className="w-8 h-8 text-status-error" />
            </div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">Invalid Link</h1>
            <p className="text-cockpit-text-secondary mb-6">
              This password reset link is invalid or has expired.
            </p>
            <Link to="/forgot-password" className="btn-primary inline-block">
              Request New Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen auth-bg flex items-center justify-center p-4" style={{ backgroundImage: 'url(/cockpit/hero.jpg)' }}>
        <div className="absolute inset-0 bg-black/80" />
        
        <div className="relative w-full max-w-md" data-testid="reset-success">
          <div className="card-panel p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-status-success/20 rounded-full mb-4">
              <CheckCircle className="w-8 h-8 text-status-success" />
            </div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">Password Reset!</h1>
            <p className="text-cockpit-text-secondary mb-6">
              Your password has been reset successfully. Redirecting to login...
            </p>
            <Link to="/login" className="btn-primary inline-block">
              Sign In Now
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-4" style={{ backgroundImage: 'url(/cockpit/hero.jpg)' }}>
      <div className="absolute inset-0 bg-black/80" />
      
      <div className="relative w-full max-w-md" data-testid="reset-password-page">
        <div className="card-panel p-8">
          {/* Header */}
          <AuthHeader title="Set New Password" subtitle="Enter your new password below" />

          {/* Error message */}
          {error && (
            <div className="mb-6 p-3 bg-red-900/30 border border-red-900 rounded-sm">
              <p className="text-sm text-red-300" data-testid="reset-error">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label-text block mb-2">New Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pr-10"
                  placeholder="••••••••"
                  required
                  data-testid="reset-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-cockpit-text-secondary hover:text-cockpit-text-primary"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="label-text block mb-2">Confirm New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field"
                placeholder="••••••••"
                required
                data-testid="reset-confirm-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2"
              data-testid="reset-submit"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
